import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createApi } from '@reduxjs/toolkit/query/react';
import { useInjectReducer } from 'utils/redux-injectors';
import { State } from './types';
import { endpoints, baseQuery } from 'utils/api/endpoints';

export const initialState: State = {
  selectedFolder: null,
  selectedFile: null,
  updatedFiles: null,
  openFolderId: localStorage.getItem('folder_id') || 0,
  folderPath: null,
  isAIGenerated: false,
  aiContent: null,
};

const slice = createSlice({
  name: 'files',
  initialState,
  reducers: {
    setSelectedFolder(state, action: PayloadAction<any>) {
      state.selectedFolder = action.payload;
    },
    setSelectedFile(state, action: PayloadAction<any>) {
      state.selectedFile = action.payload;
    },
    setOpenFolderId(state, action: PayloadAction<any>) {
      window.localStorage.setItem('folder_id', action.payload);
      state.openFolderId = action.payload;
    },
    setFolderPath(state, action: PayloadAction<any>) {
      state.folderPath = action.payload;
    },
    setIsAIGenerated(state, action: PayloadAction<any>) {
      state.isAIGenerated = action.payload;
    },
    setAiContent(state, action: PayloadAction<any>) {
      state.aiContent = action.payload;
    },
  },
});

export const api = createApi({
  reducerPath: 'filesApi',
  baseQuery,
  tagTypes: ['Files'],
  endpoints: builder => ({
    folders: builder.query<any, any>({
      query: ({ id, search, sort, order }) => {
        const folderId = id || localStorage.getItem('folder_id') || 0;
        const baseUrl = `${endpoints.folders.url}`;
        const queryParams = new URLSearchParams();
        queryParams.append('folder_id', folderId.toString());

        if (search) {
          queryParams.append('search', search);
        }

        if (sort) {
          queryParams.append('sort', sort);
        }

        if (order) {
          queryParams.append('order', order);
        }

        return {
          url: `${baseUrl}?${queryParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: ['Files'],
    }),
    addFile: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.addFile.url,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Files'],
    }),
    getFile: builder.query<any, string>({
      query: id => ({
        url: `${endpoints.GET_FILE}/${id}`,
        method: 'GET',
      }),
    }),
    addFolder: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.addFolder.url,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Files'],
    }),
    deleteFolder: builder.mutation<any, any>({
      query: id => ({
        url: `${endpoints.DELETE_FOLDER}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Files'],
    }),
    updateFolder: builder.mutation<any, any>({
      query: body => ({
        url: `${endpoints.UPDATE_FOLDER}/${body.folder_id}`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: ['Files'],
    }),
    deleteFile: builder.mutation<any, any>({
      query: id => ({
        url: `${endpoints.DELETE_FILE}/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Files'],
    }),
    updateFile: builder.mutation<any, any>({
      query: file => ({
        url: `${endpoints.UPDATE_FILE}/${file.file_id}`,
        method: 'PUT',
        body: file,
      }),
      invalidatesTags: ['Files'],
    }),

    cutCopyFile: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.CUT_COPY_FILE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Files'],
    }),

    cutAndCopyFolder: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.CUT_COPY_FOLDER,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Files'],
    }),

    folderPath: builder.query<any, any>({
      query: id => {
        const folderId = id || localStorage.getItem('folder_id') || 0;
        return {
          url: `${endpoints.FOLDER_PATH}/${folderId}`,
          method: 'GET',
        };
      },
    }),
    searchFiles: builder.query<any, any>({
      query: queryParams => {
        const params = new URLSearchParams(queryParams).toString();
        console.log(params);

        return {
          url: `${endpoints.SEARCH_FILES}?${params}`,
          method: 'GET',
        };
      },
    }),

    searchAllFiles: builder.query<any, any>({
      query: queryParams => {
        const params = new URLSearchParams(queryParams).toString();
        return {
          url: `${endpoints.SEARCH_ALL_FILES}?${params}`,
          method: 'GET',
        };
      },
    }),

    // New search endpoints
    quickSearch: builder.query<any, any>({
      query: ({ query, type = 0 }) => {
        const params = new URLSearchParams();
        params.append('query', query);
        if (type !== 0) {
          params.append('type', type.toString());
        }
        return {
          url: `${endpoints.SEARCH_FILES}?${params.toString()}`,
          method: 'GET',
        };
      },
    }),

    advancedSearch: builder.query<any, any>({
      query: ({
        query,
        type = 0,
        page = 1,
        limit = 20,
        sortBy = 'date',
        sortOrder = 'DESC',
      }) => {
        const params = new URLSearchParams();
        params.append('query', query);
        if (type !== 0) {
          params.append('type', type.toString());
        }
        params.append('page', page.toString());
        params.append('limit', limit.toString());
        params.append('sortBy', sortBy);
        params.append('sortOrder', sortOrder);
        return {
          url: `${endpoints.folders.url}/search/all?${params.toString()}`,
          method: 'GET',
        };
      },
    }),

    searchCounts: builder.query<any, any>({
      query: ({ query }) => {
        const params = new URLSearchParams();
        params.append('query', query);
        return {
          url: `${endpoints.folders.url}/search/counts?${params.toString()}`,
          method: 'GET',
        };
      },
    }),
    generateSeoTagsWithAi: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.generateSeoTagsWithAi.url,
        method: endpoints.generateSeoTagsWithAi.method,
        body: data,
      }),
    }),
    deleteFolders: builder.mutation<any, any>({
      query: (folderIds: number[]) => ({
        url: endpoints.DELETE_FOLDER,
        method: 'POST',
        body: { folder_ids: folderIds },
      }),
      invalidatesTags: ['Files'],
    }),
    bulkCreateFolders: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.BULK_CREATE_FOLDERS,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Files'],
    }),
    bulkDeleteFolderFiles: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.FOLDER_FILES_BULK_DELETE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Files'],
    }),
    bulkCutCopyPaste: builder.mutation<any, any>({
      query: data => ({
        url: endpoints.BULK_CUT_COPY_PASTE,
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['Files'],
    }),
  }),
});

export const { actions: FilesActions } = slice;

export const useFilesSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectReducer({ key: api.reducerPath, reducer: api.reducer });
  return {
    actions: FilesActions,
    useGetFoldersQuery: api.useLazyFoldersQuery,
    useAddFileMutation: api.useAddFileMutation,
    useAddFolderMutation: api.useAddFolderMutation,
    useDeleteFolderMutation: api.useDeleteFolderMutation,
    useDeleteFoldersMutation: api.useDeleteFoldersMutation,
    useDeleteFileMutation: api.useDeleteFileMutation,
    useGetFileQuery: api.useLazyGetFileQuery,
    useUpdateFileMutation: api.useUpdateFileMutation,
    useUpdateFolderMutation: api.useUpdateFolderMutation,
    useCutCopyFileMutation: api.useCutCopyFileMutation,
    useCutAndCopyFolderMutation: api.useCutAndCopyFolderMutation,
    useFolderPathQuery: api.useLazyFolderPathQuery,
    useSearchFilesQuery: api.useLazySearchFilesQuery,
    useSearchAllFilesQuery: api.useLazySearchAllFilesQuery,
    useGenerateSeoTagsWithAiMutation: api.useGenerateSeoTagsWithAiMutation,
    useBulkCreateFoldersMutation: api.useBulkCreateFoldersMutation,
    useBulkDeleteFolderFilesMutation: api.useBulkDeleteFolderFilesMutation,
    useBulkCutCopyPasteMutation: api.useBulkCutCopyPasteMutation,
    // New search hooks
    useQuickSearchQuery: api.useLazyQuickSearchQuery,
    useAdvancedSearchQuery: api.useLazyAdvancedSearchQuery,
    useSearchCountsQuery: api.useLazySearchCountsQuery,
  };
};
